import { useMemo } from "react";
import { useAtomValue } from "jotai";
import { useRouterState } from "@tanstack/react-router";
import {
  accessibilityTagsAtom,
  desktopHomePageAccessibilityTagsAtom,
} from "common/hooks/business/home/atoms";
import { useSearchFilterSections } from "common/hooks/business/home/useSearchFilterSections";
import {
  searchPageSections,
  splitStepFreeAlternatives,
  type FilterSectionLike,
  type FilterSectionTag,
} from "@/lib/accessibilityFilterState";

/**
 * The sections the narrow-width filter sheet draws: the search's own on /search, else the search
 * page's list, else home's.
 *
 * @returns The sections as listed, the same with the step-free alternatives split out, and those alternatives.
 */
export function useFilterSheetSections(): {
  sections: FilterSectionLike[];
  preparedData: FilterSectionLike[];
  tagsToAdd: FilterSectionTag[];
} {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const scoped = searchPageSections(pathname, useSearchFilterSections());
  const searchTags = useAtomValue(accessibilityTagsAtom);
  const homeTags = useAtomValue(desktopHomePageAccessibilityTagsAtom);
  const sections: FilterSectionLike[] = scoped ?? (searchTags.length > 0 ? searchTags : homeTags);

  return useMemo(() => ({ sections, ...splitStepFreeAlternatives(sections) }), [sections]);
}
